import type { PairScoreDoc } from '@astrally/shared';
import type { MatchableProfile } from './profile.js';
import { checkEligibility, type HardFilterContext } from './hardFilters.js';
import { computePairScore, type PairScoreExtras } from './pairScore.js';

/**
 * Candidate ranking. IMPORTANT SCALE PROPERTY: this never compares every user
 * with every other user. The caller supplies a bounded `pool` (a Firestore
 * query by region/intents/activity, or the demo seed) — typically ≤ 200
 * profiles — and we score only seeker×pool, keeping the top few.
 */

export interface ScoredCandidate {
  candidate: MatchableProfile;
  pair: PairScoreDoc & { extras: PairScoreExtras };
}

export interface RankInput {
  seeker: MatchableProfile;
  pool: MatchableProfile[];
  ctx: HardFilterContext;
  limit?: number;
  now?: Date;
}

export interface RankResult {
  top: ScoredCandidate[];
  excluded: { uid: string; reasons: string[] }[];
}

export function rankCandidates({ seeker, pool, ctx, limit = 5, now }: RankInput): RankResult {
  const excluded: RankResult['excluded'] = [];
  const scored: ScoredCandidate[] = [];

  for (const candidate of pool) {
    const eligibility = checkEligibility(seeker, candidate, ctx);
    if (!eligibility.eligible) {
      excluded.push({ uid: candidate.uid, reasons: eligibility.reasons });
      continue;
    }
    scored.push({ candidate, pair: computePairScore(seeker, candidate, now) });
  }

  scored.sort((x, y) => y.pair.overall.score - x.pair.overall.score);
  return { top: scored.slice(0, limit), excluded };
}
